import React, { useMemo } from "react";
import { Brush, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { getLineColor } from "../../../utils/getLineColor";
import { Colors } from "../../../constants/colors";

function transformChartData(rawData, lines) {
    const transformed = [];

    rawData.forEach((item) => {
        const key = lines?.find((line) => line.id === item.lineId)?.label  || `${item?.name} - ${item?.values?.join(", ")}`;

        let existing = transformed.find((d) => d.label === item.label);
        if (!existing) {
            existing = { label: item?.label };
            transformed.push(existing);
        }

        existing[key] = parseFloat(item?.average);
    });

    return transformed;
}

const MetadataShareOfVoiceOverTimeChart = ({ chartData, widget, smoothLine, lines }) => {
    const transformedData = useMemo(() => transformChartData(chartData, lines), [chartData, lines]);

    const lineKeys = useMemo(
        () => Object.keys(transformedData[0] || {}).filter((key) => key !== "label"),
        [transformedData]
    );
    const rawValues = transformedData.flatMap((dataPoint) =>
        lineKeys.map((key) => dataPoint[key])
    );
    const rawMin = Math.min(...rawValues);
    const rawMax = Math.max(...rawValues);

    const minScore = Math.floor(rawMin );
    const maxScore = Math.ceil(rawMax );

    return (
        <div style={{ height: widget.layout.h * 39 - 80 }} onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault()
        }}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart
                    data={transformedData}
                    margin={{
                        top: 10,
                        right: 30,
                        left: 0,
                        bottom: 0,
                    }}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey="label"
                        tick={{ fontSize: 16, fill: "#333", fontWeight: "500" }}
                        label={{
                            position: "insideBottom",
                            dy: 20,
                            fontSize: 16,
                            fill: "#555",
                        }}
                        interval="preserveStartEnd"
                    />
                    <YAxis
                        label={{
                            value: `Percentage (%)`,
                            angle: -90,
                            position: "insideLeft",
                            offset: 20,
                            style: {
                                textAnchor: "middle",
                                fontSize: `16px`,
                                fontWeight: "bold",
                                color: "#000",
                            },
                        }}
                        tick={{ fontSize: 16, fill: "#333" }}
                        domain={[minScore, maxScore]}
                    />
                    <Tooltip
                        contentStyle={{
                            backgroundColor: "#f5f5f5",
                            borderRadius: "10px",
                        }}
                        labelStyle={{ fontWeight: "bold" }}
                        formatter={(value) => `${value}%`}
                    />
                    <Legend
                        align="center"
                        verticalAlign="bottom"
                        wrapperStyle={{
                            fontSize: "14px",
                            fontWeight: "bold",
                            marginTop: "20px",
                        }}
                    />
                    <defs>
                        <linearGradient id="brushGreyGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#B0B0B0" stopOpacity={0.7} />
                            <stop offset="50%" stopColor="#D3D3D3" stopOpacity={0.5} />
                            <stop offset="100%" stopColor="#F5F5F5" stopOpacity={0.3} />
                        </linearGradient>
                    </defs>
                    <Brush
                        dataKey="label"
                        height={35}
                        stroke={Colors.gray7}
                        fill="url(#brushGreyGradient)"
                        travellerWidth={28}
                        travellerStyle={{
                            stroke: "#555",
                            fill: "#777",
                            cursor: "pointer",
                        }}
                        selectedBoxStyle={{
                            stroke: "#555",
                            fill: "rgba(175,168,168,0.3)",
                            cursor: "move",
                        }}
                        gap={1}
                    />
                    {lineKeys.map((key, index) => (
                        <Line
                            key={key}
                            type={smoothLine ? "monotone" : "linear"}
                            dataKey={key}
                            stroke={getLineColor(index)}
                            strokeWidth={3}
                            dot={false}
                            activeDot={{
                                r: 6,
                                stroke: getLineColor(index),
                                strokeWidth: 2,
                            }}
                            name={key}
                        />
                    ))}
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

export default MetadataShareOfVoiceOverTimeChart;
